import React, { useState } from "react";
import {
    HiOutlineSearch,
    HiOutlineFilter,
    HiPlus,
    HiOutlineCalendar,
    HiOutlineCash,
    HiOutlineChartBar,
    HiOutlineCheck,
    HiOutlineClock,
    HiOutlinePause,
    HiChevronDown
} from "react-icons/hi";
import Button from "../shared/Button";
import TextInput from "../shared/TextInput";
import Card from "../shared/Card";
import Badge from "../shared/Badge";
import { getEffectiveStatus } from "../../utils/campaignStatus";

const STATUS_VARIANTS = {
    running: "success",
    paused: "warning",
    completed: "default",
    scheduled: "dark",
};

export const CampaignGrid = ({ campaigns = [], onCampaignClick, onCreateCampaign, onDeleteCampaign, onEditCampaign, isAdmin = true }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [sortBy] = useState("name");
    const [sortDirection] = useState("asc");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [animatingCards, setAnimatingCards] = useState({});

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleStatusFilter = (status) => {
        setFilterStatus(status);
        setIsFilterOpen(false);
    };

    const handleCampaignClick = (campaign) => {
        setAnimatingCards(prev => ({
            ...prev,
            [campaign.id]: true
        }));

        setTimeout(() => {
            setAnimatingCards(prev => ({
                ...prev,
                [campaign.id]: false
            }));
            onCampaignClick(campaign);
        }, 300);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "running":
                return <HiOutlineChartBar className="w-3.5 h-3.5 mr-1" />;
            case "completed":
                return <HiOutlineCheck className="w-3.5 h-3.5 mr-1" />;
            case "paused":
                return <HiOutlinePause className="w-3.5 h-3.5 mr-1" />;
            case "scheduled":
                return <HiOutlineClock className="w-3.5 h-3.5 mr-1" />;
            default:
                return null;
        }
    };

    const processedCampaigns = campaigns
        ?.map((campaign) => ({ ...campaign, effectiveStatus: getEffectiveStatus(campaign) }))
        .filter((campaign) => {
            const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                campaign.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                campaign.effectiveStatus.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === "all" || campaign.effectiveStatus === filterStatus;

            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            if (sortBy === "name") {
                return sortDirection === "asc"
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name);
            } else if (sortBy === "date") {
                return sortDirection === "asc"
                    ? new Date(a.startDate) - new Date(b.startDate)
                    : new Date(b.startDate) - new Date(a.startDate);
            } else if (sortBy === "points") {
                const aPoints = (a.submissions || []).reduce((sum, s) => sum + (s.value || 0), 0);
                const bPoints = (b.submissions || []).reduce((sum, s) => sum + (s.value || 0), 0);
                return sortDirection === "asc" ? aPoints - bPoints : bPoints - aPoints;
            } else if (sortBy === "transactions") {
                return sortDirection === "asc"
                    ? (a.submissions || []).length - (b.submissions || []).length
                    : (b.submissions || []).length - (a.submissions || []).length;
            }
            return 0;
        });

    const filterOptions = [
        { value: "all", label: "All Campaigns" },
        { value: "running", label: "Running" },
        { value: "paused", label: "Paused" },
        { value: "completed", label: "Completed" },
        { value: "scheduled", label: "Scheduled" },
    ];

    const totalPointsAcross = campaigns?.reduce((sum, campaign) =>
        sum + (campaign.submissions || []).reduce((s, sub) => s + (sub.value || 0), 0), 0) || 0;

    return (
        <div className="p-6 min-h-screen font-sans bg-canvas">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-display-md text-ink" data-help-title="Campaigns" data-help="Campaigns are the rules that turn customer activity into loyalty points. You define when a member earns points, how many they earn, and how to keep things fair.">Campaigns</h1>
                        <p className="text-body-md text-muted mt-1">Build and run loyalty programs across your organisation.</p>
                    </div>
                    {isAdmin && (
                        <Button
                            variant="primary"
                            leadingIcon={<HiPlus className="w-5 h-5" />}
                            onClick={onCreateCampaign}
                            data-help-title="New campaign"
                            data-help="Opens the campaign builder to create a new loyalty campaign with a name, dates, conditions, rewards, and fraud-prevention limits. Only admins can create campaigns."
                        >
                            New campaign
                        </Button>
                    )}
                </div>

                {/* Stats grid (3-up demo grid feel) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card variant="surface-soft" className="p-6" data-help-title="Active campaigns" data-help="Shows how many campaigns are currently running and awarding points to qualifying submissions.">
                        <p className="text-body-md text-muted mb-2">Active campaigns</p>
                        <p className="text-display-md text-ink">
                            {campaigns?.filter(c => getEffectiveStatus(c) === "running").length || 0}
                        </p>
                    </Card>
                    <Card variant="cream-callout" className="p-6" data-help-title="Total points awarded" data-help="Shows the total number of points awarded across all campaign submissions in your organisation.">
                        <p className="text-body-md text-ink/70 mb-2">Total points awarded</p>
                        <p className="text-display-md text-ink">
                            {totalPointsAcross.toLocaleString()}
                        </p>
                    </Card>
                    <Card variant="default" className="p-6" data-help-title="All campaigns" data-help="Shows the total number of campaigns your organisation has created, regardless of status.">
                        <p className="text-body-md text-muted mb-2">All campaigns</p>
                        <p className="text-display-md text-ink">
                            {campaigns?.length || 0}
                        </p>
                    </Card>
                </div>
            </div>

            {/* Search and filters */}
            <Card variant="default" className="p-4 mb-6">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-grow max-w-xl">
                        <TextInput
                            leadingIcon={<HiOutlineSearch className="w-5 h-5" />}
                            placeholder="Search campaigns by name, status, or description..."
                            value={searchTerm}
                            onChange={handleSearch}
                            data-help-title="Search campaigns"
                            data-help="Filters the campaign grid as you type, matching against campaign name, description, or status."
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                leadingIcon={<HiOutlineFilter className="w-5 h-5" />}
                                trailingIcon={<HiChevronDown className="w-5 h-5" />}
                                data-help-title="Status filter"
                                data-help="Opens a list of statuses to narrow the grid: All Campaigns, Running, Paused, Completed, or Scheduled."
                            >
                                Filter
                            </Button>

                            {isFilterOpen && (
                                <div className="absolute right-0 z-20 mt-2 w-48 rounded-md bg-canvas border border-hairline shadow-card overflow-hidden">
                                    <div className="py-1" role="menu" aria-orientation="vertical">
                                        {filterOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => handleStatusFilter(option.value)}
                                                className="block w-full text-left px-4 py-2 text-body-md text-ink hover:bg-surface-soft transition-colors"
                                                role="menuitem"
                                                data-help-title="Status option"
                                                data-help="Shows only campaigns with this status. Choose All Campaigns to clear the filter."
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Campaign Grid */}
            {processedCampaigns?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {processedCampaigns.map((campaign) => {
                        const totalPoints = (campaign.submissions || []).reduce(
                            (sum, submission) => sum + (submission.value || 0),
                            0
                        );
                        const totalTransactions = (campaign.submissions || []).length;

                        return (
                            <Card
                                key={campaign.id}
                                variant="default"
                                onClick={() => handleCampaignClick(campaign)}
                                className={`cursor-pointer overflow-hidden transition-all duration-200 hover:bg-surface-soft ${
                                    animatingCards[campaign.id] ? 'transform scale-[1.02]' : ''
                                }`}
                                data-help-title="Campaign card"
                                data-help="Summarizes one campaign with its status, dates, points awarded, and transaction count. Click to open the campaign and view or edit its conditions, rewards, and fraud-prevention rules."
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <h2 className="text-title-md text-ink line-clamp-2 pr-2">
                                        {campaign.name}
                                    </h2>
                                    <Badge variant={STATUS_VARIANTS[campaign.effectiveStatus] || "default"} data-help-title="Campaign status" data-help="Shows the campaign's current status: Running campaigns award points, Paused campaigns are on hold, Scheduled campaigns have not started yet, and Completed campaigns have ended.">
                                        <span className="inline-flex items-center">
                                            {getStatusIcon(campaign.effectiveStatus)}
                                            {campaign.effectiveStatus.charAt(0).toUpperCase() + campaign.effectiveStatus.slice(1)}
                                        </span>
                                    </Badge>
                                </div>

                                {campaign.description && (
                                    <p className="text-body-md text-muted mb-4 line-clamp-2">
                                        {campaign.description}
                                    </p>
                                )}

                                <div className="text-body-md space-y-2">
                                    {campaign.startDate && campaign.endDate && (
                                        <div className="flex items-center text-muted">
                                            <HiOutlineCalendar className="w-4 h-4 mr-2" />
                                            <span>
                                                {new Date(campaign.startDate).toLocaleDateString()} -{" "}
                                                {new Date(campaign.endDate).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center">
                                        <HiOutlineCash className="w-4 h-4 mr-2 text-muted" />
                                        <span className="text-ink font-medium">{totalPoints.toLocaleString()}</span>
                                        <span className="ml-1 text-muted">points awarded</span>
                                    </div>
                                    <div className="flex items-center">
                                        <HiOutlineChartBar className="w-4 h-4 mr-2 text-muted" />
                                        <span className="text-ink font-medium">{totalTransactions}</span>
                                        <span className="ml-1 text-muted">transactions</span>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <Card variant="default" className="p-10 text-center" data-help-title="No campaigns found" data-help="Appears when no campaigns match your search or filter, or when your organisation has not created any campaigns yet.">
                    <div className="inline-flex items-center justify-center p-4 rounded-full mb-4 bg-surface-soft">
                        <HiOutlineSearch className="w-8 h-8 text-muted" />
                    </div>
                    <h3 className="text-title-sm text-ink mb-2">No campaigns found</h3>
                    <p className="max-w-md mx-auto mb-6 text-body-md text-muted">
                        {searchTerm || filterStatus !== "all"
                            ? "Try adjusting your search or filters to find what you're looking for."
                            : "Get started by creating your first campaign."}
                    </p>
                    {isAdmin && (!searchTerm && filterStatus === "all") && (
                        <div className="inline-flex">
                            <Button
                                variant="primary"
                                leadingIcon={<HiPlus className="w-5 h-5" />}
                                onClick={onCreateCampaign}
                                data-help-title="New campaign"
                                data-help="Opens the campaign builder to create your first campaign. Only admins can create campaigns."
                            >
                                New campaign
                            </Button>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};
